import { ShieldCheck, Key, Plus, Check, Activity, Inbox, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AdminAPI } from "@/lib/api";
import { useEffect, useState } from "react";
import { toast } from "sonner";

const colorCycle = [
  "bg-forest/10 text-forest border-forest/30",
  "bg-amber-warm/15 text-amber-700 border-amber-warm/30",
  "bg-sage/20 text-forest border-sage/40",
  "bg-terracotta/10 text-terracotta border-terracotta/30",
  "bg-stone-soft/40 text-ink/75 border-stone-soft",
];
const keyOf = (m, a) => `${m}|${a}`;

export default function AdminRoles() {
  const [modules, setModules] = useState([]);
  const [roles, setRoles] = useState([]);
  const [selected, setSelected] = useState(null);      // role yang sedang diedit
  const [perms, setPerms] = useState({});              // "module|action" -> bool
  const [loadingPerms, setLoadingPerms] = useState(false);
  const [saving, setSaving] = useState(false);
  const [logs, setLogs] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [newRole, setNewRole] = useState("");

  const loadCatalog = (selectRole) =>
    AdminAPI.rbacCatalog().then((d) => {
      setModules(d.modules || []);
      setRoles(d.roles || []);
      const pick = selectRole || selected || (d.roles?.[0]?.role);
      if (pick) setSelected(pick);
    }).catch(() => {}).finally(() => setLoaded(true));

  useEffect(() => {
    loadCatalog();
    AdminAPI.auditLogs().then((l) => setLogs(l || [])).catch(() => {});
  }, []); // eslint-disable-line

  // Muat matriks izin tiap kali peran terpilih berubah.
  useEffect(() => {
    if (!selected) return;
    setLoadingPerms(true);
    AdminAPI.rbacPermissions(selected).then((d) => {
      const map = {};
      (d.permissions || []).forEach((p) => { map[keyOf(p.module, p.action)] = !!p.allowed; });
      setPerms(map);
    }).catch(() => setPerms({})).finally(() => setLoadingPerms(false));
  }, [selected]);

  const toggle = (m, a) => setPerms((prev) => ({ ...prev, [keyOf(m, a)]: !prev[keyOf(m, a)] }));

  const save = async () => {
    setSaving(true);
    try {
      const permissions = [];
      modules.forEach((mod) => mod.actions.forEach((a) => permissions.push({ module: mod.module, action: a, allowed: perms[keyOf(mod.module, a)] ? 1 : 0 })));
      await AdminAPI.saveRbacPermissions(selected, permissions);
      toast.success(`Izin peran ${roleLabel(selected)} tersimpan`);
    } catch (err) {
      toast.error(err?.response?.data?.error || "Gagal menyimpan izin");
    } finally { setSaving(false); }
  };

  const addRole = async () => {
    if (!newRole.trim()) return toast.error("Isi nama peran");
    try {
      const res = await AdminAPI.createRole({ label: newRole.trim() });
      toast.success(`Peran "${newRole.trim()}" dibuat`);
      setAddOpen(false); setNewRole("");
      await loadCatalog(res.role);
    } catch (err) {
      toast.error(err?.response?.data?.error || "Gagal membuat peran");
    }
  };

  const roleLabel = (key) => roles.find((r) => r.role === key)?.label || key;
  const audit = (logs || []).map((l) => ({ actor: l.actor_name || "Sistem", role: l.actor_role || "-", action: l.action_type, time: new Date(l.created_at).toLocaleString("id-ID") }));

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl lg:text-4xl font-bold text-ink tracking-tight">Peran & RBAC</h1>
          <p className="text-ink/75 mt-2 text-sm">Atur hak akses berlapis untuk pengurus, admin, dan operator. Perubahan tersimpan ke database.</p>
        </div>
        <Button className="rounded-lg bg-forest hover:bg-forest-dark text-white" onClick={() => setAddOpen(true)} data-testid="roles-add">
          <Plus size={15} /> Tambah Peran
        </Button>
      </div>

      {/* Role cards — klik untuk memilih peran yang diedit */}
      {loaded && roles.length === 0 ? (
        <Card className="rounded-xl border-stone-soft p-10 text-center">
          <Inbox className="mx-auto text-ink/30" size={28} /><p className="text-sm text-ink/55 mt-2">Belum ada peran</p>
        </Card>
      ) : (
      <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
        {roles.map((r, i) => (
          <button
            key={r.role}
            onClick={() => setSelected(r.role)}
            data-testid={`role-card-${r.role.toLowerCase()}`}
            className={`text-left rounded-xl border p-5 transition ${colorCycle[i % colorCycle.length]} ${selected === r.role ? "ring-2 ring-forest ring-offset-1" : "opacity-90 hover:opacity-100"}`}
          >
            <ShieldCheck size={20} />
            <div className="font-display font-semibold mt-3">{r.label}</div>
            <div className="text-xs opacity-75 mt-1">{r.count} pengguna{r.system ? "" : " · kustom"}</div>
          </button>
        ))}
      </div>
      )}

      {/* Matriks izin peran terpilih */}
      <Card className="rounded-xl border-stone-soft p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="font-display font-semibold text-ink">Matriks Izin: {selected ? roleLabel(selected) : "—"}</h3>
            <p className="text-xs text-ink/55">Aktif/nonaktifkan izin per modul, lalu simpan.</p>
          </div>
          <Button onClick={save} disabled={saving || loadingPerms || !selected} className="rounded-lg bg-forest hover:bg-forest-dark text-white" data-testid="roles-save">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Simpan Perubahan
          </Button>
        </div>
        {loadingPerms ? (
          <div className="py-10 text-center text-sm text-ink/55"><Loader2 size={20} className="animate-spin mx-auto mb-2" /> Memuat izin…</div>
        ) : (
          <div className="space-y-4">
            {modules.map((p) => (
              <div key={p.module} className="rounded-xl border border-stone-soft p-4">
                <div className="font-display font-semibold text-sm text-ink mb-3 flex items-center gap-2">
                  <Key size={14} className="text-amber-warm" /> {p.module}
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  {p.actions.map((a) => (
                    <div key={a} className="flex items-center justify-between bg-cream rounded-lg p-2.5">
                      <span className="text-xs text-ink">{a}</span>
                      <Switch
                        checked={!!perms[keyOf(p.module, a)]}
                        onCheckedChange={() => toggle(p.module, a)}
                        data-testid={`perm-${p.module.toLowerCase().replace(/\s+/g, "-")}-${a.toLowerCase().replace(/\s+/g, "-")}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Audit trail */}
      <Card className="rounded-xl border-stone-soft p-6">
        <div className="flex items-center gap-2 mb-4">
          <Activity className="text-amber-warm" size={17} />
          <h3 className="font-display font-semibold text-ink">Audit Trail</h3>
        </div>
        <Table>
          <TableHeader>
            <TableRow className="border-stone-soft bg-cream/40 hover:bg-cream/40">
              <TableHead className="text-[10px] uppercase tracking-wider">Pelaku</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider">Peran</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider">Aksi</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider text-right">Waktu</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {audit.map((a, i) => (
              <TableRow key={i} className="border-stone-soft hover:bg-cream/30" data-testid={`audit-row-${i}`}>
                <TableCell className="font-semibold text-sm text-ink">{a.actor}</TableCell>
                <TableCell><Badge className="bg-stone-soft/40 text-ink/75 hover:bg-stone-soft/40 border-0 rounded-full text-[10px] uppercase tracking-wider">{a.role}</Badge></TableCell>
                <TableCell className="text-sm text-ink/75">{a.action}</TableCell>
                <TableCell className="text-right text-xs text-ink/55">{a.time}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {audit.length === 0 && (
          <div className="py-10 text-center"><Inbox className="mx-auto text-ink/30" size={26} /><p className="text-sm text-ink/55 mt-2">Belum ada aktivitas tercatat</p></div>
        )}
      </Card>

      {/* Dialog Tambah Peran */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-display">Tambah Peran Kustom</DialogTitle>
            <DialogDescription>Peran baru dibuat dengan seluruh izin nonaktif — atur di matriks lalu simpan.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5 mt-2">
            <Label className="text-xs uppercase tracking-wider text-ink/55">Nama Peran</Label>
            <Input value={newRole} onChange={(e) => setNewRole(e.target.value)} placeholder="cth. Operator Gudang" className="rounded-lg" data-testid="add-role-name" />
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setAddOpen(false)}>Batal</Button>
            <Button className="rounded-full bg-forest hover:bg-forest-dark text-white" onClick={addRole} data-testid="add-role-submit">Buat Peran</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
